import React, { useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";


import { useDispatch } from "react-redux";
import { addProfileData } from "../../../../redux/slices/profileslice";
import { editProfile, profileDetails } from "../../services/user.service";
import PhoneInput from "../../../../utils/PhoneInput";
import { SWAL_SETTINGS, ROLE } from "../../../../utils/Constants";
import { handleServerValidations, getUser, setLocalKey } from "../../../../utils/commonfunction";

function SellerEditProfile() {
  const dispatch = useDispatch();
  const isAdvertiserContext = window.location.href.includes("/advertiser");
  const fallbackRole = isAdvertiserContext ? ROLE.ADVERTISER : ROLE.USER;
  let userData = getUser(ROLE.USER);
  if (!userData || Object.keys(userData).length === 0) {
    userData = getUser(ROLE.ADVERTISER) || {};
  }

  const [previewImage, setPreviewImage] = useState(userData?.profile_image || "/images/User.png");

  // ---- Phone Validation ----
  const PHONE_VALIDATION = Yup.string()
    .test("required", "Phone number is required", (value) => !!value)
    .test("digits", "Phone must contain digits only", (value) => {
      const number = value?.split(" ")[1] || "";
      return /^\d+$/.test(number);
    })
    .test("min_len", "Phone number is too short", (value) => {
      const number = value?.split(" ")[1] || "";
      return number.length >= 4;
    })
    .test("max_len", "Phone number is too long", (value) => {
      const number = value?.split(" ")[1] || "";
      return number.length <= 15;
    });

  // ---- Validation Schema ----
  const validationSchema = Yup.object().shape({
    name: Yup.string().required("Name is required"),
    display_name: Yup.string().required("Display name is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
    address: Yup.string().required("Address is required"),
    phone_number: PHONE_VALIDATION,
    whatsapp_phone_number: PHONE_VALIDATION,
    profile_image: Yup.mixed().nullable(),
  });

  // ---- Initial Values ----
  const initialValues = {
    name: userData?.name || "",
    display_name: userData?.display_name || "",
    email: userData?.email || "",
    address: userData?.address || "",
    phone_number: userData?.phone_number || "",
    whatsapp_phone_number: userData?.whatsapp_phone_number || "",
    profile_image: userData?.profile_image,
  };

  // ---- Submit Handler ----
  const handleSubmit = (values, { setSubmitting }) => {
    let formData = new FormData();

    formData.append("_id", userData?._id);

    // Build FormData + Track changed fields
    let changedData = {};

    Object.keys(values).forEach((key) => {
      // Add to FormData
      formData.append(key, values[key]);
      // Compare with old data → Save only changed fields
      if (values[key] !== userData[key]) {
        changedData[key] = values[key];
      }
    });

    editProfile(formData)
      .then((response) => {
        if (response.success) {
          Swal.fire({
            icon: "success",
            text: response.message,
            ...SWAL_SETTINGS,
          });
          profileDetails().then((response) => {
            console.log(response.data);
            if (response.data) {
              const existingUser = getUser(ROLE.USER) || {};
              const mergedData = { ...existingUser, ...response.data };
              dispatch(addProfileData(mergedData));
              setLocalKey(ROLE.USER, JSON.stringify(mergedData));
              setLocalKey(ROLE.ADVERTISER, JSON.stringify(mergedData));
            }
          })
            .catch((err) => { console.log(err) })

          // Merge updated (changed only) fields into localStorage advertiser
          //           const updatedUser = { ...userData, ...changedData };
          // 
          //           localStorage.setItem("advertiser", JSON.stringify(updatedUser));
        } else {
          Swal.fire({
            icon: "error",
            text: handleServerValidations(response),
            ...SWAL_SETTINGS,
          });
        }
      })
      .catch((error) => console.log(error))
      .finally(() => setSubmitting(false));
  };

  return (
    <>
      <div className="max-w-[100%] mx-auto">
        <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
          {({ values, setFieldValue, isSubmitting }) => (
            <Form>
              {/* Header */}
              <div className="user-profile-bg bg-primary-50 p-[15px] rounded-[10px] flex items-center justify-between gap-[15px] flex-wrap">
                <div className="user-image-title flex items-center gap-[10px]">
                  <img src={previewImage} className="w-[89px] h-[89px] object-cover rounded-full" alt="user" />
                  <h2 className="headings-h2 text-zinc-950">{values.name || "User"}</h2>
                </div>

                <label className="btn btn-pri cursor-pointer">
                  Upload Photo
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        setFieldValue("profile_image", file);
                        setPreviewImage(URL.createObjectURL(file));
                      }
                    }}
                  />
                </label>
              </div>

              {/* Form Fields */}
              <div className="edit-profile-form-bg mt-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-[15px]">
                  <div className="form-group">
                    <label>Name</label>
                    <Field type="text" name="name" placeholder="Enter name" className="form-control-custom" />
                    <ErrorMessage name="name" component="p" className="text-red-500 text-sm" />
                  </div>

                  <div className="form-group">
                    <label>Display Name</label>
                    <Field type="text" name="display_name" placeholder="Enter display name" className="form-control-custom" />
                    <ErrorMessage name="display_name" component="p" className="text-red-500 text-sm" />
                  </div>
                </div>

                <div className="form-group">
                  <label>Email</label>
                  <Field type="email" name="email" placeholder="Enter email" disabled className="form-control-custom bg-gray-100 cursor-not-allowed" />
                  <ErrorMessage name="email" component="p" className="text-red-500 text-sm" />
                </div>

                <div className="form-group">
                  <label>Address</label>
                  <Field type="text" name="address" placeholder="Enter address" className="form-control-custom" />
                  <ErrorMessage name="address" component="p" className="text-red-500 text-sm" />
                </div>

                <div className="form-group">
                  <label>Phone Number</label>
                  <PhoneInput
                    value={values.phone_number}
                    onChange={(val) => {
                      const finalValue = typeof val === "object" ? `${val.code} ${val.number}` : val;
                      setFieldValue("phone_number", finalValue);
                    }}
                  />
                  <ErrorMessage name="phone_number" component="p" className="text-red-500 text-sm" />
                </div>

                <div className="form-group">
                  <label>WhatsApp Contact Number</label>
                  <PhoneInput
                    value={values.whatsapp_phone_number}
                    onChange={(val) => {
                      const finalValue = typeof val === "object" ? `${val.code} ${val.number}` : val;
                      setFieldValue("whatsapp_phone_number", finalValue);
                    }}
                  />
                  <ErrorMessage name="whatsapp_phone_number" component="p" className="text-red-500 text-sm" />
                </div>

                <button className="btn btn-pri min-w-[181px]" type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Updating..." : "Update"}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </>
  );
}

export default SellerEditProfile;
